import { Box, BoxProps, CircularProgress, Skeleton } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useFirebase } from '@/firebase';

interface SecureImageProps extends Omit<BoxProps<'img'>, 'src'> {
  src: string;
  path?: string;
  fallback?: React.ReactNode;
}

/**
 * A secure image component that resolves Firebase Storage paths to authenticated download URLs.
 * Falls back to direct src if resolution fails or if src is already a full URL.
 */
export function SecureImage({ src, path, fallback, sx, ...props }: SecureImageProps) {
  const { backend } = useFirebase();
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function resolve() {
      if (!backend) {
        setResolvedUrl(src);
        setLoading(false);
        return;
      }

      if (path) {
        try {
          const url = await backend.assets.resolveSecureAssetUrl(path);
          if (isMounted) {
            setResolvedUrl(url);
            setLoading(false);
          }
          return;
        } catch (err) {
          console.warn(`[SecureImage] Failed to resolve path: ${path}`, err);
          // Fall through to try resolving src
        }
      }

      if (src) {
        try {
          // resolveSecureAssetUrl will automatically extract the path from raw storage URLs
          // or refresh expired firebasestorage URLs.
          const url = await backend.assets.resolveSecureAssetUrl(src);
          if (isMounted) {
            setResolvedUrl(url);
            setLoading(false);
          }
        } catch (err) {
          // If resolution completely fails, fallback to original src
          if (isMounted) {
            setResolvedUrl(src);
            setLoading(false);
          }
        }
      } else if (isMounted) {
        setLoading(false);
      }
    }

    resolve();

    return () => {
      isMounted = false;
    };
  }, [src, path, backend]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '100%', 
          height: '100%',
          bgcolor: 'action.hover',
          ...sx 
        }}
      >
        <CircularProgress size={24} color="inherit" sx={{ opacity: 0.5 }} />
      </Box>
    );
  }

  if (error || !resolvedUrl) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '100%', 
          height: '100%',
          bgcolor: 'action.disabledBackground',
          ...sx 
        }}
      >
        {fallback}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={resolvedUrl}
      onError={() => setError(true)}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        ...sx
      }}
      {...props}
    />
  );
}
